import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Youtube, Wand2, ImageIcon, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Wand2, title: "SEO Titles & Scripts", desc: "Turn a rough idea into a click-worthy title, keyword-rich description and a scene-by-scene breakdown." },
  { icon: ImageIcon, title: "Thumbnails & Scenes", desc: "Generate a high-CTR thumbnail and cinematic scene art with GPT Image 1 in one click." },
  { icon: Sparkles, title: "Your Style, Everywhere", desc: "Upload a reference image and every generated visual matches your look. Edit prompts freely." },
];

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/studio", { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="grain relative min-h-screen bg-[#0b0c10] text-[#f3f4f6] overflow-hidden">
      <div className="pointer-events-none absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#00e5ff] opacity-[0.07] blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#00e5ff] opacity-[0.05] blur-[120px]" />

      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-md bg-white">
            <img src={`${process.env.PUBLIC_URL}/ytf-logo.png`} alt="YouTube Factory" className="h-full w-full object-contain" />
          </div>
          <span className="font-display text-lg font-bold tracking-tight">YouTube Factory <span className="text-[#00e5ff]">AI</span></span>
        </div>
        <Button
          data-testid="login-button-top"
          onClick={login}
          className="rounded-md bg-[#00e5ff] text-black hover:bg-[#33ebff] font-semibold"
        >
          Sign in with Google
        </Button>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 md:px-12 pt-16 pb-24">
        <p className="overline mb-5">AI YouTube Production Assistant</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-none tracking-tight max-w-3xl">
          From A Single Idea To{" "}
          <span className="text-[#00e5ff]">Ready To Post Content.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base md:text-lg text-[#9ca3af] leading-relaxed">
          Enter short or long form story idea. Get an SEO-optimized title, description and scene descriptions,
          then generate matching thumbnail and scene images — all editable, all yours.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-4">
          <Button
            data-testid="login-button-hero"
            onClick={login}
            disabled={loading}
            size="lg"
            className="group rounded-md bg-[#00e5ff] text-black hover:bg-[#33ebff] font-semibold text-base h-12 px-7"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <>
                Get started
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </Button>
          <span className="text-sm text-[#9ca3af]">Invite-only · authorized accounts</span>
        </div>

        <div className="mt-20 grid gap-5 md:grid-cols-3">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-lg border border-[#272a35] bg-[#14161c] p-6 transition-transform duration-200 hover:-translate-y-1 hover:bg-[#1d2028]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#272a35] text-[#00e5ff]">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display mt-4 text-lg font-semibold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-[#9ca3af] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
